import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import { faXmark } from "@fortawesome/free-solid-svg-icons"

type SystemFloatingAlertVariant = "danger" | "warning"

export interface SystemFloatingAlertItem {
  id: string
  title: string
  description: string
  closeLabel: string
  icon: IconProp
  variant: SystemFloatingAlertVariant
  onClose: () => void
  onClick?: () => void
}

interface SystemFloatingAlertsProps {
  items: SystemFloatingAlertItem[]
}

const variantClasses: Record<
  SystemFloatingAlertVariant,
  {
    container: string
    icon: string
    close: string
  }
> = {
  danger: {
    container: "border-red-300 bg-red-50 text-red-950 ring-red-200 animate-[pulse_1.2s_ease-in-out_infinite]",
    icon: "bg-red-600 text-white",
    close: "text-red-900 hover:bg-red-100",
  },
  warning: {
    container: "border-amber-300 bg-amber-50 text-amber-950 ring-amber-200",
    icon: "bg-amber-500 text-white",
    close: "text-amber-900 hover:bg-amber-100",
  },
}

export const SystemFloatingAlerts: React.FC<SystemFloatingAlertsProps> = ({ items }) => {
  if (items.length === 0) {
    return null
  }

  return (
    <div className="fixed bottom-20 right-3 z-[970] flex w-[calc(100vw-1.5rem)] max-w-[420px] flex-col-reverse gap-3 md:bottom-6 md:right-6">
      {items.map((item) => {
        const classes = variantClasses[item.variant]

        return (
          <section
            key={item.id}
            onClick={item.onClick}
            className={`relative rounded-xl border-2 p-4 shadow-2xl ring-2 ${item.onClick ? "cursor-pointer transition hover:-translate-y-0.5" : ""} ${classes.container}`}
            role={item.onClick ? "button" : undefined}
            tabIndex={item.onClick ? 0 : undefined}
            onKeyDown={(event) => {
              if (!item.onClick || (event.key !== "Enter" && event.key !== " ")) {
                return
              }

              event.preventDefault()
              item.onClick()
            }}
            data-interaction-description-key="interactionDescriptions:components.systemFloatingAlerts.systemFloatingAlerts.button.itemOnClick"
          >
            <div className="flex items-start gap-3 pr-8">
              <div
                className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full shadow-md ${classes.icon}`}
              >
                <FontAwesomeIcon icon={item.icon} />
              </div>

              <div className="min-w-0">
                <p className="text-sm font-black">{item.title}</p>

                <p className="mt-1 text-sm font-semibold leading-snug">{item.description}</p>
              </div>
            </div>

            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                item.onClose()
              }}
              className={`absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full transition-colors disabled:opacity-60 ${classes.close}`}
              aria-label={item.closeLabel}
              title={item.closeLabel}
              data-interaction-description-key="interactionDescriptions:components.systemFloatingAlerts.systemFloatingAlerts.button.itemCloseLabel"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </section>
        )
      })}
    </div>
  )
}
